// src/components/LegalPageShell.jsx
//
// Shell compartilhado por páginas institucionais públicas (LGPD,
// Termos de Uso, Transparência IA, etc.). Harmoniza com o design
// art-déco do app: tokens de cor da marca, Cormorant Garamond na
// headline (via classe global `deco-headline`), eyebrow em pill e
// card branco com sombra suave sobre fundo levemente avermelhado.
//
// Uso típico:
//   <LegalPageShell
//     eyebrow="Documento legal"
//     title="Transparência sobre o uso de Inteligência Artificial"
//     subtitle="Versão 1.0 — atualizada em 27/05/2026"
//     contactEmail="atendimento@consultamais.app"
//   >
//     <h2>Seção</h2>
//     <p>...</p>
//   </LegalPageShell>

import React from 'react';
import { useNavigate } from 'react-router-dom';
import FormBackButton from './FormBackButton';
import DecoDivider from './DecoDivider';

const SHELL_CSS = `
  .legal-page-shell {
    min-height: 100vh;
    margin: 0;
    background:
      radial-gradient(ellipse 70% 35% at 50% 0%, rgba(211,47,47,0.06) 0%, transparent 70%),
      radial-gradient(ellipse 60% 30% at 50% 100%, rgba(211,47,47,0.04) 0%, transparent 70%),
      var(--bg-page, #F9FAFB);
    font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Inter', 'Roboto', sans-serif;
    color: var(--text-main, #1E1E1E);
    -webkit-font-smoothing: antialiased;
    padding: max(4.5rem, env(safe-area-inset-top, 0px)) 1rem 4rem;
    box-sizing: border-box;
  }

  .legal-page-shell__inner {
    max-width: 760px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .legal-page-shell__header {
    text-align: center;
    margin-bottom: 1.25rem;
    max-width: 38rem;
    width: 100%;
  }

  .legal-page-shell__hero {
    width: 100%;
    max-width: 38rem;
    aspect-ratio: 16 / 7;
    object-fit: cover;
    border-radius: 1.25rem;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.10);
    margin-bottom: 1.25rem;
    display: block;
  }

  .legal-page-shell__eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: #FFF1F1;
    color: #D32F2F;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    border-radius: 999px;
    padding: 0.45rem 0.95rem;
    margin-bottom: 1rem;
    border: 1px solid rgba(211, 47, 47, 0.12);
  }

  .legal-page-shell__title {
    font-size: 1.5rem;
    line-height: 1.25;
    font-weight: 700;
    color: var(--text-main, #1E1E1E);
    margin: 0 0 0.4rem;
    letter-spacing: -0.005em;
  }

  .legal-page-shell__subtitle {
    font-size: 0.85rem;
    color: var(--text-dim, #6B6B6B);
    margin: 0;
    line-height: 1.5;
  }

  .legal-page-shell__divider {
    color: rgba(181, 138, 79, 0.55);
    margin: 0.75rem auto 0.25rem;
    max-width: 12rem;
  }

  .legal-page-shell__card {
    background: #FFFFFF;
    width: 100%;
    max-width: 38rem;
    border-radius: 1.25rem;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.04);
    padding: 1.75rem 1.4rem;
    box-sizing: border-box;
    font-size: 0.92rem;
    line-height: 1.65;
    color: var(--text-main, #1E1E1E);
  }

  .legal-page-shell__card h2 {
    font-size: 1.05rem;
    font-weight: 700;
    color: #1F2937;
    letter-spacing: -0.005em;
    margin: 1.75rem 0 0.5rem;
    padding-bottom: 0.4rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }
  .legal-page-shell__card h2:first-child {
    margin-top: 0;
  }

  .legal-page-shell__card h3 {
    font-size: 1rem;
    font-weight: 700;
    color: #374151;
    margin: 1.3rem 0 0.4rem;
  }

  .legal-page-shell__card p {
    margin: 0 0 0.85rem;
    color: #374151;
  }

  .legal-page-shell__card ul,
  .legal-page-shell__card ol {
    margin: 0 0 0.95rem;
    padding-left: 1.4rem;
    color: #374151;
  }

  .legal-page-shell__card li {
    margin-bottom: 0.5rem;
  }

  .legal-page-shell__card a {
    color: #D32F2F;
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  .legal-page-shell__card a:hover {
    color: #B71C1C;
  }

  .legal-page-shell__card code {
    font-family: 'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.85em;
    background: rgba(15, 23, 42, 0.06);
    padding: 1px 6px;
    border-radius: 4px;
    color: #1f2937;
  }

  .legal-page-shell__card strong {
    color: #1F2937;
    font-weight: 700;
  }

  .legal-page-shell__footer {
    text-align: center;
    font-size: 0.8rem;
    color: var(--text-dim, #6B6B6B);
    margin-top: 1.75rem;
    line-height: 1.55;
    max-width: 36rem;
  }

  .legal-page-shell__footer a {
    color: #D32F2F;
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  @media (min-width: 480px) {
    .legal-page-shell {
      padding: 4.5rem 2rem 5rem;
    }
    .legal-page-shell__title {
      font-size: 1.65rem;
    }
    .legal-page-shell__card {
      padding: 2.25rem 2rem;
      font-size: 0.95rem;
    }
    .legal-page-shell__card h2 {
      font-size: 1.15rem;
    }
  }

  @media (min-width: 768px) {
    .legal-page-shell__title {
      font-size: 1.8rem;
    }
    .legal-page-shell__card {
      font-size: 0.98rem;
    }
  }
`;

function LegalPageShell({
  eyebrow,
  title,
  subtitle,
  contactEmail = 'atendimento@consultamais.app',
  contactPrefix = 'Em caso de dúvidas, entre em contato em',
  showDivider = true,
  heroImage,
  heroAlt = '',
  // Destino fixo do botão Voltar. O padrão é a home: voltar por histórico
  // prende usuários em webview sem stack de navegação. Artigos, portal e
  // categorias de notícias passam "/" de propósito.
  backTo = '/',
  children,
}) {
  const navigate = useNavigate();
  const handleBack = () => navigate(backTo);

  return (
    <>
      <style>{SHELL_CSS}</style>
      <FormBackButton onClick={handleBack} />
      <div className="legal-page-shell">
        <div className="legal-page-shell__inner">
          {heroImage ? (
            <img className="legal-page-shell__hero" src={heroImage} alt={heroAlt} loading="lazy" />
          ) : null}
          <header className="legal-page-shell__header">
            {eyebrow ? <span className="legal-page-shell__eyebrow">{eyebrow}</span> : null}
            <h1 className="legal-page-shell__title">{title}</h1>
            {subtitle ? <p className="legal-page-shell__subtitle">{subtitle}</p> : null}
            {showDivider ? (
              <DecoDivider variant="chevron" className="legal-page-shell__divider" />
            ) : null}
          </header>

          <article className="legal-page-shell__card">{children}</article>

          {contactEmail ? (
            <p className="legal-page-shell__footer">
              {contactPrefix}{' '}
              <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
            </p>
          ) : null}
        </div>
      </div>
    </>
  );
}

export default LegalPageShell;
